import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminRole, ChannelStatus, DEFAULT_PAGE_SIZE } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/status-badge";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { Amount } from "@/components/amount";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import {
  CHANNEL_PAYMENT_STATUS_COLOR,
  CHANNEL_SETTLEMENT_STATUS_COLOR,
  CHANNEL_STATUS_COLOR,
} from "@/lib/status-colors";
import { formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import { ApiError } from "@/api/client";
import { getChannel, listChannelPayments, listChannelSettlements, settleNowChannel } from "@/api/channels";
import { type ChannelPaymentListItem, type ChannelSettlementListItem } from "@/api/types";

// Task 14 (feature-031 Phase B): the channel 360° detail page — real
// internal ChannelStatus and fireblocksVaultId visible ("the backoffice
// sees the truth" per Task 8's commit message), plus the CHANNELS_MANAGER-
// only settle-now action. Mirrors _app.invoices_.$invoiceId.tsx's shape
// (Summary card + action button in the header, sub-resource tables below),
// but the payments/settlements tables are fetched independently via their
// own filterable list routes (`channelId` filter) rather than embedded in
// the detail response — matches channels.response.ts's own doc comment
// ("payments/settlements are read via their own filterable list routes").
export const Route = createFileRoute("/_app/channels_/$channelId")({
  component: ChannelDetailPage,
});

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ─── payments sub-table (channelId filter, local pagination) ───────────────

function ChannelPaymentsTable({ channelId }: { channelId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isPending, isError } = useQuery({
    queryKey: ["channel", channelId, "payments", page, pageSize],
    queryFn: () => listChannelPayments({ channelId, page, pageSize }),
  });

  const columns: DataTableColumn<ChannelPaymentListItem>[] = [
    {
      key: "publicId",
      header: "Payment ID",
      cell: (p) => (
        <Link to="/channel-payments/$channelPaymentId" params={{ channelPaymentId: p.id }} className="group -m-2 block p-2">
          <span className="font-mono group-hover:underline">{p.publicId}</span>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (p) => (
        <StatusBadge label={formatStatusLabel(p.subStatus ?? p.status)} color={CHANNEL_PAYMENT_STATUS_COLOR[p.status]} />
      ),
    },
    {
      key: "amount",
      header: "Amount",
      cell: (p) => <Amount native={p.cryptoAmount} symbol={p.asset.symbol} usd={p.cryptoAmountUsd} />,
    },
    {
      key: "senderAddress",
      header: "Sender",
      cell: (p) => <HashLink value={p.senderAddress} type="address" network={p.asset.network} className="text-xs" />,
    },
    { key: "createdAt", header: "Created", cell: (p) => formatTimestamp(p.createdAt) },
  ];

  return (
    <DataTable
      searchable={false}
      columns={columns}
      items={data?.data ?? []}
      total={data?.total ?? 0}
      page={page}
      pageSize={pageSize}
      loading={isPending}
      error={isError ? "Failed to load payments." : null}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(1);
      }}
      getRowKey={(p) => p.id}
      emptyMessage="No payments on this channel."
    />
  );
}

// ─── settlements sub-table (channelId filter, local pagination) ────────────

function ChannelSettlementsTable({ channelId }: { channelId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isPending, isError } = useQuery({
    queryKey: ["channel", channelId, "settlements", page, pageSize],
    queryFn: () => listChannelSettlements({ channelId, page, pageSize }),
  });

  const columns: DataTableColumn<ChannelSettlementListItem>[] = [
    {
      key: "publicId",
      header: "Settlement ID",
      cell: (s) => (
        <Link
          to="/channel-settlements/$channelSettlementId"
          params={{ channelSettlementId: s.id }}
          className="group -m-2 block p-2"
        >
          <span className="font-mono group-hover:underline">{s.publicId}</span>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (s) => <StatusBadge label={formatStatusLabel(s.status)} color={CHANNEL_SETTLEMENT_STATUS_COLOR[s.status]} />,
    },
    {
      key: "grossAmount",
      header: "Total Amount",
      cell: (s) => <Amount native={s.grossAmount} symbol={s.asset.symbol} />,
    },
    { key: "paymentCount", header: "Payments", cell: (s) => s.paymentCount },
    { key: "createdAt", header: "Created", cell: (s) => formatTimestamp(s.createdAt) },
  ];

  return (
    <DataTable
      searchable={false}
      columns={columns}
      items={data?.data ?? []}
      total={data?.total ?? 0}
      page={page}
      pageSize={pageSize}
      loading={isPending}
      error={isError ? "Failed to load settlements." : null}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(1);
      }}
      getRowKey={(s) => s.id}
      emptyMessage="No settlements on this channel."
    />
  );
}

function ChannelDetailPage() {
  const { channelId } = Route.useParams();
  const { admin } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const canSettleNow = hasRole(admin.roles, AdminRole.CHANNELS_MANAGER);

  const {
    data: channel,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["channel", channelId],
    queryFn: () => getChannel(channelId),
  });

  const settleNowMutation = useMutation({
    mutationFn: () => settleNowChannel(channel!.publicId),
    onSuccess: (settlement) => {
      queryClient.invalidateQueries({ queryKey: ["channel", channelId] });
      toast.success(`Settlement ${settlement.publicId} opened.`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to open a settlement.");
    },
  });

  if (isPending) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Loading channel…</div>;
  }
  if (isError || !channel) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Failed to load channel.</div>;
  }

  // Same eligibility shape as ChannelsService.settleNow's own guard (ACTIVE
  // or ARCHIVED, with a provisioned address) — the backend is the source of
  // truth for the exact rule; this just avoids offering a button that would
  // always 409.
  const settleNowEligible =
    (channel.status === ChannelStatus.ACTIVE || channel.status === ChannelStatus.ARCHIVED) && channel.address !== null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{channel.publicId}</h2>
          <p className="text-sm text-muted-foreground">
            {channel.merchant.businessName} · {channel.merchant.email}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge label={formatStatusLabel(channel.status)} color={CHANNEL_STATUS_COLOR[channel.status]} />
          {canSettleNow && settleNowEligible && (
            <Button type="button" size="sm" disabled={settleNowMutation.isPending} onClick={() => settleNowMutation.mutate()}>
              {settleNowMutation.isPending ? "Opening…" : "Settle now"}
            </Button>
          )}
        </div>
      </div>

      <SectionCard title="Summary">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b pb-4">
          <div>
            <div className="text-lg font-semibold">{channel.label}</div>
            {channel.externalId && <div className="mt-0.5 text-sm text-muted-foreground">{channel.externalId}</div>}
          </div>
          <AssetLabel symbol={channel.asset.symbol} network={channel.asset.network} inline />
        </div>
        <dl className="space-y-2 text-sm">
          <DetailRow
            label="Deposit address"
            value={<HashLink value={channel.address} type="address" network={channel.asset.network} />}
          />
          <DetailRow label="Fireblocks vault ID" value={channel.fireblocksVaultId ?? "—"} />
          {channel.provisioningError && <DetailRow label="Provisioning error" value={channel.provisioningError} />}
          <DetailRow label="Created" value={formatTimestamp(channel.createdAt)} />
          <DetailRow label="Archived" value={formatTimestamp(channel.archivedAt)} />
        </dl>
      </SectionCard>

      <SectionCard title="Payments">
        <ChannelPaymentsTable channelId={channel.id} />
      </SectionCard>

      <SectionCard title="Settlements">
        <ChannelSettlementsTable channelId={channel.id} />
      </SectionCard>
    </div>
  );
}
